
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import ReCAPTCHA from "react-google-recaptcha";
import auBnr from "../../img/au_bnr.png";

const BASE_URL = "https://akk.net.in:4000";
const ZOHO_FORM_ACTION =
  "https://crm.zoho.in/crm/WebToLeadForm";

const ZOHO_ANALYTICS_URL =
  "https://crm.zohopublic.in/crm/WebFormAnalyticsServeServlet?rid=5486f432afa5ae6cfec1168b3fc5558ae2e664e444a2fd28c64439cec68b2a4cb0679945a4b59d61beaeb48abd25e95bgid818cfb4d686d8ce0c9d395fb8398c1090c66a3cd7d87dffc9351da6ec9be29c5gid5ef170acf77845f27fb9a9a278ed704540e7cf2bbc2aba2e87c573a6dce99a46gide4799451d87924169273fd745ac5a77d81ceff1f8eb02a2c58aab53355edfb7a&tw=91fe552f79f1ad168435cb0159faaf9b7a12e1f8072c3bdbf59761978b68ed19&version=v2";

const DEBUG_FORM = true;
const SHOW_FULL_FIELD_VALUES = false;

const debugLog = (...args) => {
  if (DEBUG_FORM) console.log(...args);
};

const initialForm = {
  name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

export default function ContactUs() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const zohoFormRef = useRef(null);
  const recaptchaRef = useRef(null);

  const currentURL = window.location.href;
  const returnURL = `${window.location.origin}/`;

  const handelChange = (e) => {
    const { name, value } = e.target;

    const fieldMap = {
      "Last Name": "name",
      Email: "email",
      Mobile: "phone",
      LEADCF33: "subject",
      Description: "message",
    };

    const fieldName = fieldMap[name];

    if (fieldName) {
      setForm((previous) => ({
        ...previous,
        [fieldName]: value,
      }));
    }
  };

  // Fetch page information
  useEffect(() => {
    const controller = new AbortController();

    const getContactUsPage = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/contactUs/getContactUsPageByUser`,
          { signal: controller.signal }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();
        setData(result.contactUsPage ?? null);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error(
            "[CONTACT] Page API error:",
            error
          );
        }
      }
    };

    getContactUsPage();

    return () => controller.abort();
  }, []);

  // Zoho analytics script
  useEffect(() => {
    window._wFa_ajax_will_be_replaced = false;

    window._wfa_fstprtcken =
      window._wfa_fstprtcken || {};

    window._wfa_fstprtcken[
      "1320227000001000185"
    ] = true;

    const existing = document.getElementById("wf_anal");
    if (existing) return;

    const script = document.createElement("script");

    script.id = "wf_anal";
    script.src = ZOHO_ANALYTICS_URL;
    script.async = true;

    script.onload = () => {
      debugLog(
        "[ZOHO ANALYTICS] Script loaded. This does not confirm lead creation."
      );
    };

    script.onerror = () => {
      console.warn(
        "[ZOHO ANALYTICS] Failed to load analytics script"
      );
    };

    document.body.appendChild(script);

    return () => script.remove();
  }, []);

  const handelSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    debugLog(
      "[CONTACT] Submit clicked:",
      new Date().toISOString()
    );

    // Validate fields
    if (Object.values(form).some((value) => !value.trim())) {
      console.warn("[CONTACT] Empty fields found");
      alert("Please fill in all fields");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(form.email.trim())) {
      console.warn("[CONTACT] Invalid email");
      alert("Please enter a valid email address");
      return;
    }

    const phoneDigits = form.phone.replace(/\D/g, "");

    if (phoneDigits.length < 10) {
      console.warn("[CONTACT] Invalid mobile number");
      alert("Please fill Valid Number");
      return;
    }

    if (!recaptchaValue) {
      console.warn("[CONTACT] reCAPTCHA missing");
      alert("Please complete the reCAPTCHA verification");
      return;
    }

    debugLog("[CONTACT] Validation passed");

    setIsSubmitting(true);

    try {
      // STEP 1 - Existing email API

      debugLog(
        "[EMAIL API] Request URL:",
        `${BASE_URL}/contactUs/sendEmail`
      );

      if (DEBUG_FORM) {
        console.table(
          Object.entries(form).map(([key, value]) => ({
            field: key,
            present: Boolean(value.trim()),
            length: value.length,
            value: SHOW_FULL_FIELD_VALUES
              ? value
              : "[hidden]",
          }))
        );
      }

      const response = await fetch(
        `${BASE_URL}/contactUs/sendEmail`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(form),
        }
      );

      debugLog(
        "[EMAIL API] HTTP Status:",
        response.status,
        response.statusText
      );

      const rawBody = await response.text();

      let result;

      try {
        result = JSON.parse(rawBody);
      } catch {
        console.error(
          "[EMAIL API] Invalid JSON:",
          rawBody.slice(0, 500)
        );
        throw new Error("Email API did not return JSON");
      }

      debugLog("[EMAIL API] Response:", result);

      if (!response.ok) {
        throw new Error(
          `Email API HTTP ${response.status}`
        );
      }

      const emailAccepted =
        result.success === "1" ||
        result.success === 1;

      debugLog(
        "[EMAIL API] Confirmed success:",
        emailAccepted
      );

      if (!emailAccepted) {
        throw new Error(
          result.message || "Email API submission failed"
        );
      }

      // STEP 2 - Validate Zoho form data

      const htmlForm = zohoFormRef.current;

      if (!htmlForm) {
        throw new Error("Zoho form element not found");
      }

      const zohoData = new FormData(htmlForm);

      const requiredFields = [
        "xnQsjsdp",
        "xmIwtLD",
        "actionType",
        "Last Name",
        "Mobile",
      ];

      const missingFields = requiredFields.filter(
        (key) => !String(zohoData.get(key) ?? "").trim()
      );

      const expectedValues = {
        "Last Name": form.name,
        Email: form.email,
        Mobile: form.phone,
        LEADCF33: form.subject,
        Description: form.message,
      };

      const mismatchedFields = Object.entries(expectedValues)
        .filter(
          ([key, value]) => zohoData.get(key) !== value
        )
        .map(([key]) => key);

      debugLog("[ZOHO] Request URL:", htmlForm.action);
      debugLog(
        "[ZOHO] HTTP Method:",
        htmlForm.method.toUpperCase()
      );
      debugLog("[ZOHO] Form ID:", htmlForm.id);

      if (DEBUG_FORM) {
        console.log("[ZOHO] Complete form field audit");

        console.table(
          [...zohoData.entries()].map(([key, value]) => ({
            field: key,
            present: String(value).trim().length > 0,
            length: String(value).length,
            value:
              key === "xnQsjsdp" ||
              key === "xmIwtLD"
                ? "[Zoho identifier hidden]"
                : SHOW_FULL_FIELD_VALUES
                  ? String(value)
                  : "[hidden]",
          }))
        );
      }

      if (
        missingFields.length > 0 ||
        mismatchedFields.length > 0
      ) {
        console.error(
          "[ZOHO] Missing required fields:",
          missingFields
        );

        console.error(
          "[ZOHO] Field mismatches:",
          mismatchedFields
        );

        throw new Error("Zoho form audit failed");
      }

      debugLog(
        "[ZOHO] Required fields and field mapping: PASS"
      );

      console.info(
        "[ZOHO] Starting browser POST submission..."
      );

      console.warn(
        "[ZOHO] CRM lead creation is NOT confirmed yet."
      );

      console.info(
        "[ZOHO] Inspect Network > WebToLeadForm for the actual response."
      );

      // Native HTML form submission
      // React cannot access the cross-origin response.
      // This also avoids calling sendEmail twice.

      htmlForm.submit();

    } catch (error) {
      console.error(
        "[CONTACT] Submission failed:",
        error
      );

      alert(
        "Unable to submit your request. Please try again."
      );

      setIsSubmitting(false);
      recaptchaRef.current?.reset();
      setRecaptchaValue(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>{data?.metaTitle || "Contact Us"}</title>

        <meta
          name="description"
          content={data?.metaDescription || ""}
        />

        <meta
          name="keywords"
          content={data?.metaKeyword || ""}
        />

        <link rel="canonical" href={currentURL} />
      </Helmet>

      <Navbar />

      {/* Banner */}
      <div
        className="heading_banner_sm"
        style={{
          backgroundImage: `url(${
            data?.bannerImage || auBnr
          })`,
        }}
      >
        <div className="container">
          <h1>Contact Us</h1>
        </div>
      </div>

      <div className="container">

        {/* Contact Information */}
        <div className="row">
          <div className="col-md-12 mt-4">
            <h2 className="a5">
              <span>Contact Information</span>
            </h2>

            <p>
              <strong>Email: </strong>

              <a
                target="_blank"
                rel="noopener noreferrer"
                href="mailto:info@akk.net.in"
              >
                info@akk.net.in
              </a>
            </p>

            <p>
              <strong>Contact Number: </strong>

              <a href="tel:+919355932486">
                +91 93559 32486
              </a>
            </p>
          </div>
        </div>

        <div className="row">

          {/* Google Map */}
          <div className="col-md-6">
            <div
              style={{
                height: "400px",
                border: "1px solid #ccc",
              }}
            >
              <iframe
                title="AKK recycling centre location"
                style={{
                  width: "100%",
                  height: "400px",
                  border: "1px solid #ccc",
                }}
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3514.293950819308!2d76.9123407761882!3d28.259102300939105!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d39e035086817%3A0x735a6b48fa4d84b7!2sAbhishek%20K%20Kaiho%20Recyclers%20Pvt.%20Ltd.%20(AKK)%20%7C%20Scrap%20car%20recycling%20center%2C%20Delhi%20NCR%2C%20Haryana%2C%20Gurgaon!5e0!3m2!1sen!2sin"
                width="100%"
                height="400"
                frameBorder="0"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Form */}
          <div className="col-md-6">
            <form
              ref={zohoFormRef}
              id="webform1320227000001000185"
              name="WebToLeads1320227000001000185"
              action={ZOHO_FORM_ACTION}
              method="POST"
              acceptCharset="UTF-8"
              onSubmit={handelSubmit}
            >
              <input
                type="hidden"
                name="xnQsjsdp"
                value="52282bc2eaabfbfcc4bde4fa2c878df3e0023fbcb85ec2dac7af6c727762774f"
                readOnly
              />

              <input
                type="hidden"
                name="zc_gad"
                id="zc_gad"
                value=""
                readOnly
              />

              <input
                type="hidden"
                name="xmIwtLD"
                value="1e30895588d68bc188557473613e77635635113f31d4dcd062f3ecc0826cf87aab399d0c9e05aa6d4aaa0817476edca7"
                readOnly
              />

              <input
                type="hidden"
                name="actionType"
                value="TGVhZHM="
                readOnly
              />

              <input
                type="hidden"
                name="returnURL"
                value={returnURL}
                readOnly
              />

              <input
                type="hidden"
                name="Company"
                value=""
                readOnly
              />

              <input
                type="hidden"
                name="aG9uZXlwb3Q"
                value=""
                readOnly
              />

              {/* Name and Email */}
              <div className="mb-3 row">
                <div className="col-md-6">
                  <label
                    htmlFor="contact-name"
                    className="form-label"
                  >
                    Name
                  </label>

                  <input
                    type="text"
                    name="Last Name"
                    value={form.name}
                    onChange={handelChange}
                    placeholder="Name"
                    className="form-control"
                    id="contact-name"
                    maxLength={80}
                    required
                  />
                </div>

                <div className="col-md-6">
                  <label
                    htmlFor="contact-email"
                    className="form-label"
                  >
                    Email
                  </label>

                  <input
                    type="email"
                    name="Email"
                    value={form.email}
                    onChange={handelChange}
                    placeholder="Email"
                    className="form-control"
                    id="contact-email"
                    maxLength={100}
                    required
                  />
                </div>
              </div>

              {/* Phone and Subject */}
              <div className="mb-3 row">
                <div className="col-md-6">
                  <label
                    htmlFor="contact-phone"
                    className="form-label"
                  >
                    Phone
                  </label>

                  <input
                    type="tel"
                    name="Mobile"
                    value={form.phone}
                    onChange={handelChange}
                    placeholder="Phone"
                    className="form-control"
                    id="contact-phone"
                    maxLength={30}
                    required
                  />
                </div>

                <div className="col-md-6">
                  <label
                    htmlFor="contact-subject"
                    className="form-label"
                  >
                    Subject
                  </label>

                  <input
                    type="text"
                    name="LEADCF33"
                    value={form.subject}
                    onChange={handelChange}
                    placeholder="Subject"
                    className="form-control"
                    id="contact-subject"
                    required
                  />
                </div>
              </div>

              {/* Message */}
              <div className="mb-3 row">
                <div className="col">
                  <label
                    htmlFor="contact-message"
                    className="form-label"
                  >
                    Message
                  </label>

                  <textarea
                    name="Description"
                    value={form.message}
                    onChange={handelChange}
                    placeholder="Message"
                    className="form-control"
                    id="contact-message"
                    rows={4}
                    required
                  />
                </div>
              </div>

              {/* reCAPTCHA */}
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey="6LdosFgpAAAAAEulS6fgoy48JE9TG2LNfhvzCdDY"
                onChange={setRecaptchaValue}
              />

              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </form>
          </div>

        </div>
      </div>

      <Footer />
    </>
  );
}
